/**
 * Turning a sheet id and a frame into CSS.
 *
 * Every sheet is shipped twice, AVIF and WebP, and every one was re-packed by
 * tools/build-art.py onto square cells -- so a frame is found by percentage
 * offsets alone, with nothing measured at runtime.
 */

import { ASSET_SRC, SHEETS, type SheetId } from './art.generated';

/**
 * The URL for a piece of art, by the name the manifest gives it.
 *
 * THE ONLY PLACE a name becomes a URL, and it has to be. The files on the
 * server carry a content hash (`valley-growing-v7.3f9a2c1e04.avif`) so that a
 * repainted file is a new URL -- /assets is cached immutably for a year -- and
 * that hash lives in the generated table. Code that writes `/assets/${name}`
 * by hand gets a 404 today and would have served stale art tomorrow.
 *
 * A name missing from the table falls back to itself, so a manifest entry the
 * build could not produce shows as a broken image rather than a crash; the
 * build has already failed loudly about it by then.
 */
export function assetUrl(file: string, ext: 'avif' | 'webp'): string {
  return `/assets/${ASSET_SRC[file] ?? file}.${ext}`;
}

/**
 * The two ways to write a sheet's background, most capable first.
 *
 * Apply them IN ORDER to the same property. A browser that does not
 * understand `image-set(... type())` rejects that assignment and keeps the
 * plain WebP set before it, so there is a working picture either way and no
 * feature detection. Safari before 17 is the one this is for.
 */
export function sheetBackgrounds(id: SheetId): [fallback: string, preferred: string] {
  const file = SHEETS[id].file;
  const webp = assetUrl(file, 'webp');
  const avif = assetUrl(file, 'avif');
  return [
    `url("${webp}")`,
    `image-set(url("${avif}") type("image/avif"), url("${webp}") type("image/webp"))`,
  ];
}

export function applySheet(el: HTMLElement, id: SheetId): void {
  const [fallback, preferred] = sheetBackgrounds(id);
  el.style.backgroundImage = fallback;
  el.style.backgroundImage = preferred;
}

/** `background-size` and `background-position` for one cell of a grid. */
export function cellCss(columns: number, rows: number, column: number, row: number) {
  // A one-column or one-row grid has nowhere to move to; dividing by zero
  // there would write `NaN%` and the browser would drop the whole rule.
  const x = columns > 1 ? (column * 100) / (columns - 1) : 0;
  const y = rows > 1 ? (row * 100) / (rows - 1) : 0;
  return {
    size: `${columns * 100}% ${rows * 100}%`,
    position: `${x}% ${y}%`,
  };
}
